import {
	createPromotion,
	handleCheckQueryPromotion,
	handleTestPromotionMail,
	updatePromotion,
} from '@/API/repositories/promotion';
import { getUsers } from '@/API/repositories/user';
import { useCommonDataContext } from '@/common/hooks/commonDataContext';
import { useRequestsContext } from '@/common/hooks/requestHook';
import { useMessageQueueContext } from '@/common/hooks/useMessageQueue';
import { useEffect, useReducer, useRef, useState } from 'react';
import { promotionElementReducer } from '../reducers/promotionElementReducer';

const usePromotionElement = ({ element, setElement }) => {
	const [initLoaded, setInitLoaded] = useState(false);
	const [filters, dispatchFilters] = useReducer(promotionElementReducer, {});
	const [selectedProduct, setSelectedProduct] = useState();
	const [productsOptions, setProductsOptions] = useState([]);
	const [selectedItem, setSelectedItem] = useState();
	const [itemOptions, setItemOptions] = useState([]);
	const [selectedPrice, setSelectedPrice] = useState();
	const [selected, setSelected] = useState([]);
	const [consultants, setConsultants] = useState([]);
	const [queryResults, setQueryResults] = useState();
	const [promotionTemplates, setPromotionTemplates] = useState(
		element.templateConfig || [],
	);
	const [editPromotionTemplate, setEditPromotionTemplate] = useState();
	const [cronTime, setCronTime] = useState(element.cron || '* * * * *');

	const nameRef = useRef();
	const activeRef = useRef();
	const isSingleUseRef = useRef();
	const includeUnsubscribeRef = useRef();
	const emailTestRef = useRef();
	const textAreaRef = useRef();

	const {
		commonData: { products, markets, queues },
	} = useCommonDataContext();

	const { addMessage } = useMessageQueueContext();
	const { makeRequest, hasUnfilledRequest } = useRequestsContext();

	const isLoading = hasUnfilledRequest(
		updatePromotion,
		createPromotion,
		getUsers,
		handleCheckQueryPromotion,
		handleTestPromotionMail,
	);

	const handleSave = async (e) => {
		e?.preventDefault();

		const payload = {
			...element,
		};

		payload.name = nameRef.current?.value;
		payload.cron = cronTime;
		payload.active = activeRef.current?.checked || false;
		payload.filters = JSON.stringify(filters);
		payload.templateConfig = promotionTemplates;
		payload.price = parseInt(selectedPrice?.value);
		payload.product = selectedProduct?.value?._id;
		payload.itemShort = selectedItem?.value;
		payload.isSingleUse = isSingleUseRef.current?.checked || false;
		payload.includeUnsubscribe =
			includeUnsubscribeRef.current?.checked || false;

		if (payload._id) {
			const response = await makeRequest(updatePromotion.bind(null, payload));

			if (response?.data) {
				addMessage('Promotion updated', 'success');
				setElement(() => null);
			}
			return;
		}

		const response = await makeRequest(createPromotion.bind(null, payload));

		if (response?.data) {
			addMessage('Promotion created', 'success');
			setElement(() => null);
		}
	};

	const handleLoad = async () => {
		const responseConsultant = await makeRequest(getUsers);

		if (responseConsultant.data) {
			setConsultants(() =>
				responseConsultant.data.map((data) => {
					return { label: data.username, value: data._id };
				}),
			);
		}

		if (!element.filters) {
			return;
		}

		dispatchFilters({
			type: 'init',
			data: JSON.parse(element.filters),
		});

		setSelected(() =>
			Object.entries(JSON.parse(element.filters)).map(([key, value]) => {
				return {
					label: key,
					value: { ...value, type: value.type_field },
				};
			}),
		);
	};

	const handleCheckQuery = async () => {
		const payload = {
			query: {
				...filters,
				includeUnsubscribe: includeUnsubscribeRef.current?.checked || false,
			},
			data: {
				mailConfigId: element?._id,
			},
		};

		const responseQueryPromotion = await makeRequest(
			handleCheckQueryPromotion.bind(null, payload),
		);

		if (responseQueryPromotion.data) {
			setQueryResults(() => responseQueryPromotion.data);
		}
	};

	const handleTest = async (e) => {
		e?.preventDefault();

		const payload = {
			email: emailTestRef?.current?.value,
			category_id: element?._id,
			mail_payload: textAreaRef.current.value,
		};

		const responseSend = await makeRequest(
			handleTestPromotionMail.bind(null, payload),
		);

		if (responseSend.data) {
			addMessage('Promotion mail send', 'success');
		} else {
			addMessage('Error', 'error');
		}
	};

	useEffect(() => {
		handleLoad();
		setProductsOptions(() =>
			products.map((d) => ({ label: d.name, value: d })),
		);
	}, [products]);

	useEffect(() => {
		if (element.product && productsOptions?.length > 0) {
			const currentProduct = productsOptions.find(
				(p) => p.value._id === element.product,
			);

			setSelectedProduct(currentProduct);

			const price = currentProduct.value.prices.find(
				(p) => p.value === element.price?.toString(),
			);

			if (!price) {
				return;
			}

			setSelectedPrice({ label: price.value, value: price.value });
		}
		setInitLoaded(true);
	}, [productsOptions]);

	useEffect(() => {
		if (!initLoaded) {
			return;
		}

		setSelectedItem(null);
		setSelectedPrice(null);

		setItemOptions(() => [
			{
				label: 'CURRENT',
				value: null,
			},
			...selectedProduct.value.variants.map((item) => ({
				label: item.short,
				value: item.short,
			})),
		]);

		if (selectedProduct.value._id === element.product) {
			setSelectedItem(
				element.itemShort
					? {
							label: element.itemShort,
							value: element.itemShort,
					  }
					: {
							label: 'CURRENT',
							value: null,
					  },
			);
			setSelectedPrice({ label: element.price, value: element.price });
		}
	}, [selectedProduct]);

	return {
		isLoading,
		handleSave,
		nameRef,
		selectedProduct,
		selectedPrice,
		setSelectedPrice,
		activeRef,
		isSingleUseRef,
		productsOptions,
		setSelectedProduct,
		itemOptions,
		selectedItem,
		setSelectedItem,
		cronTime,
		setCronTime,
		setEditPromotionTemplate,
		promotionTemplates,
		consultants,
		markets,
		products,
		queues,
		filters,
		dispatchFilters,
		selected,
		setSelected,
		handleCheckQuery,
		textAreaRef,
		emailTestRef,
		handleTest,
		editPromotionTemplate,
		setPromotionTemplates,
		queryResults,
		setQueryResults,
		includeUnsubscribeRef,
	};
};

export default usePromotionElement;
